import { Link } from '@tanstack/react-router'
import { Blocks, Database, PanelsTopLeft, Table, type LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { TemplateCategory, TemplateCounts, TemplateListItem } from '@/api/types'
import { Badge } from '@/shared/ui/badge'

export function TemplateCard({
  template,
  categories,
}: {
  template: TemplateListItem
  categories: readonly TemplateCategory[]
}) {
  const { t } = useTranslation()
  const labels = new Map(categories.map((category) => [category.id, category.label]))

  return (
    <Link
      to="/templates/$templateId"
      params={{ templateId: template.id }}
      className="flex h-full flex-col overflow-hidden rounded-xl bg-card text-left shadow-sm ring-1 ring-foreground/10 transition-shadow outline-none hover:shadow-md focus-visible:ring-3 focus-visible:ring-ring/50"
    >
      <div className="flex flex-1 flex-col gap-2 p-5">
        <h2 className="line-clamp-2 text-lg font-medium">{template.title}</h2>
        <p className="line-clamp-3 text-sm text-muted-foreground">
          {template.description}
        </p>
        {template.categories.length > 0 && (
          <div className="mt-auto flex flex-wrap gap-1.5 pt-3">
            {template.categories.map((category) => (
              <Badge key={category} variant="secondary">
                {labels.get(category) ?? category}
              </Badge>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center gap-4 border-t bg-linear-to-b from-primary/3 to-primary/10 px-5 py-3 text-sm text-muted-foreground">
        <CountsIcons counts={template.counts} />
        {template.source === 'company' && (
          <span className="ml-auto">{t('templates.companyBadge')}</span>
        )}
      </div>
    </Link>
  )
}

/** What a template creates as icon and number, named for screen readers and on hover. */
function CountsIcons({ counts }: { counts: TemplateCounts }) {
  const { t } = useTranslation()
  const parts: [LucideIcon, number, string][] = [
    [
      Database,
      counts.databases,
      t('templates.count.databases', { count: counts.databases }),
    ],
    [Table, counts.tables, t('templates.count.tables', { count: counts.tables })],
    [
      PanelsTopLeft,
      counts.dashboards,
      t('templates.count.dashboards', { count: counts.dashboards }),
    ],
    [Blocks, counts.widgets, t('templates.count.widgets', { count: counts.widgets })],
  ]
  return parts
    .filter(([, count]) => count > 0)
    .map(([Icon, count, label]) => (
      <span key={label} title={label} className="flex items-center gap-1.5">
        <Icon className="size-4" aria-hidden />
        <span aria-hidden>{count}</span>
        <span className="sr-only">{label}</span>
      </span>
    ))
}

/** What a template creates, largest parts first, leaving out what it has none of. */
export function CountsText({ counts }: { counts: TemplateCounts }) {
  const { t } = useTranslation()
  const parts = [
    counts.databases > 0 && t('templates.count.databases', { count: counts.databases }),
    counts.tables > 0 && t('templates.count.tables', { count: counts.tables }),
    counts.dashboards > 0 &&
      t('templates.count.dashboards', { count: counts.dashboards }),
    counts.widgets > 0 && t('templates.count.widgets', { count: counts.widgets }),
  ].filter((part) => part !== false)
  return <>{parts.join(' · ')}</>
}
